/**
 * Achievements Module Interfaces
 * Type definitions for achievements upload, maker-checker reports, and related entities
 */

/**
 * Achievement Item (uploaded achievement record)
 */
export interface Achievement {
  id?: number;
  user_count: number;
  total_achievement: number;
  success_achievement: number;
  updated_date: string;
  created_date?: string;
  download_achievement_file?: string;
  start_date?: string;
  end_date?: string;
}

/**
 * Achievement List Response
 */
export interface GetAchievementsListResponse {
  status: number;
  data: {
    achivement_list: Achievement[];
    message?: string;
  };
}

/**
 * Upload Excel Achievements Request
 */
export interface UploadAchievementsExcelRequest {
  excel_data: any[];
}

/**
 * Upload Excel Achievements Response (with status counts)
 */
export interface UploadAchievementsExcelResponse {
  status: number;
  body: {
    data: {
      message: string;
      import_count: number;
      uploaded_count: number;
      rejected_count?: number;
      rejected_achievement?: RejectedAchievement[];
    };
  };
}

/**
 * Rejected Achievement Record
 */
export interface RejectedAchievement {
  sku: string;
  outlet_code: string;
  quantity: number;
  sales_date: string;
  reason: string;
}

/**
 * Download Achievements Excel Response
 */
export interface DownloadAchievementsExcelResponse {
  status: number;
  data: {
    msg: string;
    url: string;
  };
}

/**
 * Target for Achievements
 */
export interface AchievementTarget {
  target_id: number | string;
  target_name: string;
  target_title?: string;
  type_of_target: 0 | 1 | 2 | 3; // 0: No Target, 1: Month, 2: Quarter, 3: Cycle
  type_of_target_name?: string;
}

/**
 * Get Targets Response
 */
export interface GetTargetsResponse {
  status: number;
  data: AchievementTarget[];
}

/**
 * Cycle for Achievements
 */
export interface AchievementCycle {
  id: number;
  target_cycle_name: string;
}

/**
 * Quarter for Achievements
 */
export interface AchievementQuarter {
  id: number;
  quarter_name: string;
}

/**
 * Brand for Achievements Template
 */
export interface AchievementBrand {
  id: number;
  sku: string;
  brand_name: string;
  status: number;
  is_loyalty: number;
}

/**
 * Batch Process Item
 */
export interface BatchProcess {
  id: number;
  target_id: number;
  target_title: string;
  batch_status: 1 | 2 | 3; // 1: Pending, 2: Success, 3: Failed
  batch_status_name: string;
  uploaded_excel?: string;
  rejected_excel?: string;
  created_date: string;
  updated_date: string;
}

/**
 * Get Batch Process List Response
 */
export interface GetBatchProcessListResponse {
  status: number;
  data: BatchProcess[];
}

/**
 * Upload Achievements File Request (FormData)
 */
export interface UploadAchievementsFileRequest {
  target_id: number | string;
  excel_file: File;
  no_target: '0' | '1';
}

/**
 * Upload Achievements File Response
 */
export interface UploadAchievementsFileResponse {
  status: number;
  body: {
    data: {
      message: string;
      total_count?: number;
      approved_count?: number;
      rejected_count?: number;
      rejected_reason?: any[];
    };
  };
}

/**
 * Maker-Checker Report
 */
export interface MakerCheckerReport {
  id: number;
  report_name: string;
  report_type: string;
  report_type_id: number;
  target_title?: string;
  target_type_name?: string;
  ledger_type_name?: string;
  target_id?: number;
  scheme_name?: string;
  month_name?: string;
  quarter_name?: string;
  year?: number;
  region_name?: string;
  state_name?: string;
  cityname?: string;
  total_outlet_count?: number;
  total_users?: number;
  approved_invoice_count?: number;
  processed_users?: number;
  total_points?: number;
  total_creditpoints?: number;
  created_date: string;
  approve_status: 0 | 1 | 2 | 3; // 0: Pending, 1: Generated, 2: Approved, 3: Rejected
  approve_status_name: string;
  status: string;
  excel_path?: string;
  approval_excel_path?: string;
}

/**
 * Get Maker-Checker Reports Response
 */
export interface GetMakerCheckerReportsResponse {
  status: number;
  data: MakerCheckerReport[];
}

/**
 * Generate Maker-Checker Report Request
 */
export interface GenerateMakerCheckerReportRequest {
  report_name: string;
  type: 'target' | 'scheme';
  month?: string;
  year?: number;
  report_type?: number;
  quarter_id?: number;
  region?: string;
  state?: string;
  city?: string;
  target_id?: string | number;
  scheme_id?: string | number;
}

/**
 * Generate Maker-Checker Report Response
 */
export interface GenerateMakerCheckerReportResponse {
  status: number;
  data?: string;
  body?: {
    data: string;
  };
}

/**
 * Generate Maker-Checker Request (Wizard Step 2)
 */
export interface GenerateMakerCheckerRequest {
  report_name: string;
  type: 'target' | 'scheme';
  target_id: string | number;
  no_target_type_id?: 1 | 2; // 1: Achievement, 2: Qr Based
}

/**
 * Approve Report Request
 */
export interface ApproveReportRequest {
  achivement_id: number;
  approve_status: 1 | 3; // 1: Approve, 3: Reject
}

/**
 * Reject Points Request
 */
export interface RejectPointsRequest {
  id: number;
  target_id: number;
  approve_status: 3;
}

/**
 * Maker Checker Campaign (for settlements list page)
 */
export interface MakerCheckerCampaign {
  id: string | number;
  campaign_id: string;
  campaign_name: string;
  region: string;
  campaign_start_date: string;
  campaign_end_date: string;
  maker_checker_type: 'Program Payout' | 'Schemes' | 'Target' | 'Scheme';
  status: 'Full' | 'Partial' | 'Pending' | 'Active' | 'Inactive';
  progress: number; // 0-100
  total_eligible_payout: number;
  sales_data_available: 'Yes' | 'No' | 'N/A';
  payout_points?: string | null;
  payout_credit_points?: string | null;
  payout_percentage?: string | null;

  // Additional fields from API
  target_id?: number;
  target_name?: string;
  description?: string;
  created_date?: string;
  updated_date?: string;
  type_of_based?: number;
  type_of_based_name?: string;
  type_of_target?: number;
  type_of_target_name?: string;
  month?: number;
  year?: number;
  quarter_id?: number | null;
  quarter_name?: string | null;
  target_cycle_name?: string | null;
  cycle_id?: number | null;
  user_type_id?: number;
  user_group_id?: number;
  qty_type_name?: string;
  achievement_logic_name?: string;
  achievement_logic?: string | null;
  ledger_type?: number;
  ledger_type_name?: string;
  notify_flag?: number;
  notification_content?: string | null;
  payout?: {
    points: string | null;
    credit_points: string | null;
    percentage: string | null;
  };
}

/**
 * Get Maker Checker Campaigns Response
 */
export interface GetMakerCheckerCampaignsResponse {
  status: number;
  data: MakerCheckerCampaign[];
}

/**
 * Get Bonus Points Response
 */
export interface GetBonusPointsResponse {
  status: number;
  data: {
    message: string;
    excel?: string;
  };
}

/**
 * Modify Checker & Maker Points Request
 */
export interface ModifyPointsRequest {
  program_id: number;
  id: number;
  excel_data: any[];
}

/**
 * Download Scheme Excel Response
 */
export interface DownloadSchemeExcelResponse {
  status: number;
  data: {
    excel_path: string;
  };
}

/**
 * Report Type
 */
export interface ReportType {
  id: number;
  name: string;
}

/**
 * Quarter Month (for dropdown)
 */
export interface QuarterMonth {
  id: number;
  name: string;
  months: MonthItem[];
}

/**
 * Month Item
 */
export interface MonthItem {
  id: number;
  name: string;
}
